import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// US Atlas TopoJSON — states
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// State name → abbreviation lookup (for matching TopoJSON property names to DB state codes)
const STATE_NAME_TO_ABBR = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR",
  "California": "CA", "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE",
  "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID",
  "Illinois": "IL", "Indiana": "IN", "Iowa": "IA", "Kansas": "KS",
  "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
  "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS",
  "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV",
  "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
  "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK",
  "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT",
  "Vermont": "VT", "Virginia": "VA", "Washington": "WA", "West Virginia": "WV",
  "Wisconsin": "WI", "Wyoming": "WY", "District of Columbia": "DC",
};

function getStateColor(summary) {
  if (!summary || summary.total === 0) return "#e2e8f0"; // no schools — light slate
  const { success, partial, failed, total } = summary;
  const successRate = success / total;
  const partialRate = (success + partial) / total;

  if (successRate >= 0.7)   return "#10b981"; // emerald — high success
  if (successRate >= 0.4)   return "#34d399"; // emerald lighter
  if (partialRate >= 0.6)   return "#f59e0b"; // amber — mostly partial
  if (partialRate >= 0.3)   return "#fbbf24"; // amber lighter
  if (failed / total >= 0.5) return "#f87171"; // red — mostly failed
  return "#fcd34d";                            // yellow — mixed
}

function StatePill({ label, color, count }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-slate-600">
      <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: color }} />
      {label}: <strong>{count}</strong>
    </span>
  );
}

export default function AdminDataHealthMap({ adminToken }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [tipContent, setTipContent] = useState("");
  const [tipPos, setTipPos]     = useState({ x: 0, y: 0 });
  const [tipVisible, setTipVisible] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/data-health/coverage-map`, { headers });
      setData(r.data);
    } catch {
      toast.error("Failed to load coverage map data");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400 text-sm">
        Loading coverage map…
      </div>
    );
  }

  const stateSummary = data?.state_summary || {};
  const schools      = data?.schools || [];

  // Aggregate totals for the legend
  const totals = { success: 0, partial: 0, failed: 0, not_attempted: 0, flagged: 0 };
  for (const s of schools) {
    if (s.has_flag) totals.flagged++;
    if (s.status === "success")   totals.success++;
    else if (s.status === "partial" || s.status === "pending_ai") totals.partial++;
    else if (s.status === "failed" || s.status === "skipped")     totals.failed++;
    else                                                          totals.not_attempted++;
  }

  return (
    <div className="space-y-4" data-testid="coverage-map-panel">
      {/* Summary pills */}
      <div className="flex flex-wrap gap-4 p-4 bg-white rounded-2xl border border-slate-200">
        <StatePill label="Success"       color="#10b981" count={totals.success} />
        <StatePill label="Partial"       color="#f59e0b" count={totals.partial} />
        <StatePill label="Failed"        color="#f87171" count={totals.failed} />
        <StatePill label="Not attempted" color="#e2e8f0" count={totals.not_attempted} />
        {totals.flagged > 0 && (
          <span className="flex items-center gap-1.5 text-xs text-amber-700 font-semibold">
            <AlertTriangle size={12} /> {totals.flagged} flagged
          </span>
        )}
        <span className="ml-auto text-xs text-slate-400">{schools.length} schools total</span>
      </div>

      {/* Map */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden relative">
        <div className="px-5 pt-4 pb-1 flex items-center justify-between">
          <h3 className="font-bold text-slate-900 text-sm">US Coverage Map</h3>
          <p className="text-xs text-slate-400">Colour = scrape success rate per state · Hover for details</p>
        </div>

        {/* Custom floating tooltip */}
        {tipVisible && (
          <div
            className="pointer-events-none fixed z-50 bg-slate-900 text-white text-xs rounded-lg px-3 py-1.5 shadow-xl max-w-xs"
            style={{ left: tipPos.x + 12, top: tipPos.y - 8 }}
          >
            {tipContent}
          </div>
        )}

        <ComposableMap
          projection="geoAlbersUsa"
          style={{ width: "100%", height: "auto" }}
          data-testid="us-map"
        >
          <ZoomableGroup>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const stateName = geo.properties.name;
                  const abbr      = STATE_NAME_TO_ABBR[stateName];
                  // DB stores full state names; fall back to abbreviation just in case
                  const summary   = stateSummary[stateName] || (abbr ? stateSummary[abbr] : null);
                  const fill      = getStateColor(summary);

                  const content = summary && summary.total > 0
                    ? `${stateName} (${abbr}): ${summary.success} success · ${summary.partial} partial · ${summary.failed} failed / ${summary.total} schools`
                    : `${stateName}${abbr ? ` (${abbr})` : ""}: no schools`;

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fill}
                      stroke="#fff"
                      strokeWidth={0.5}
                      onMouseEnter={(e) => {
                        setTipContent(content);
                        setTipPos({ x: e.clientX, y: e.clientY });
                        setTipVisible(true);
                      }}
                      onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setTipVisible(false)}
                      style={{
                        default:  { outline: "none" },
                        hover:    { outline: "none", opacity: 0.75, cursor: "pointer" },
                        pressed:  { outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* State breakdown table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="font-bold text-slate-900 text-sm">State Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-slate-400 uppercase tracking-wide bg-slate-50">
                <th className="text-left px-4 py-2 font-semibold">State</th>
                <th className="text-right px-4 py-2 font-semibold">Total</th>
                <th className="text-right px-4 py-2 font-semibold text-emerald-600">Success</th>
                <th className="text-right px-4 py-2 font-semibold text-amber-600">Partial</th>
                <th className="text-right px-4 py-2 font-semibold text-red-500">Failed</th>
                <th className="text-right px-4 py-2 font-semibold">Rate</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stateSummary)
                .filter(([, s]) => s.total > 0)
                .sort((a, b) => b[1].total - a[1].total)
                .map(([stateName, s]) => {
                  const rate = s.total ? Math.round((s.success / s.total) * 100) : 0;
                  return (
                    <tr key={stateName} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 font-semibold text-slate-700">{stateName}</td>
                      <td className="px-4 py-2 text-right text-slate-600">{s.total}</td>
                      <td className="px-4 py-2 text-right text-emerald-600 font-semibold">{s.success}</td>
                      <td className="px-4 py-2 text-right text-amber-600 font-semibold">{s.partial}</td>
                      <td className="px-4 py-2 text-right text-red-500 font-semibold">{s.failed}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={`font-bold ${rate >= 70 ? "text-emerald-600" : rate >= 40 ? "text-amber-600" : "text-red-500"}`}>
                          {rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
